import {
  createContext,
  createRef,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { fabric } from "fabric";
import {
  Filters,
  initCustomGrayscale,
  initCustomInvert,
  initCustomSepia,
  renderCornerBL,
  renderCornerBR,
  renderCornerMB,
  renderCornerML,
  renderCornerMR,
  renderCornerMT,
  renderCornerTL,
  renderCornerTR,
  renderFabricDefaultCorner,
  UndoRedoHistory,
} from "../Component/ADSContent/Component/ADSContentRight/Component/ADSPlayground/constant/imageEditor.constant";
import axios from "axios";
import ImageEditorUnsavedChangesModal from "../Component/ADSContent/Component/ADSContentRight/Component/ADSPlayground/Component/ADSPlaygroundContent/Component/ImageEditor/ImageEditorUnsavedChangesModal";
import { useADSContext } from "./ADSProvider";
import { useAIToolsContext } from "./AIToolsProvider";

const ImageEditorContext = createContext(null);

const canvasRef = createRef();
const fabricRef = createRef();
const fabricImage = createRef();
const historyRef = createRef();
const isEraserActive = createRef();

const maxCanvasWidth = 528;
const maxCanvasHeight = 650;

const defaultSliderValue = {
  brightness: 50,
  contrast: 50,
  saturation: 50,
  temperature: 50,
  grey: 50,
  sepia: 50,
  invert: 70,
  blur: 50,
};

export const useImageEditorContext = () => useContext(ImageEditorContext);

const ImageEditorContextProvider = ({ children }) => {
  const { selectedProduct, handleProductCreationSuccess, setGlobalLoading } =
    useADSContext();
  const { selectedAITools, setSelectedAITools, resetAITools } =
    useAIToolsContext();

  const [isEditingImage, setIsEditingImage] = useState(false);
  const [ActiveToolbarId, setActiveToolbarId] = useState({
    ParentId: "",
    ChildId: "",
  });
  const [RenderedImageHeight, setRenderedImageHeight] = useState(0);
  const [TextSize, setTextSize] = useState(25);
  const [TextColor, setTextColor] = useState("#000000");
  const [SliderVal, setSliderVal] = useState(defaultSliderValue);
  const [DrawSize, setDrawSize] = useState(6); // 3 | 6 | 9
  const [DrawColor, setDrawColor] = useState("#000000");
  const [IsCropping, setIsCropping] = useState(false);
  const [HasUndo, setHasUndo] = useState(false);
  const [HasRedo, setHasRedo] = useState(false);
  const [ImageScale, setImageScale] = useState(1);
  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false);

  const ShowSlider = [
    "brightness",
    "contrast",
    "saturation",
    "temperature",
    "grey",
    "sepia",
    "invert",
    "blur",
  ].includes(ActiveToolbarId.ChildId);

  const ShowTextControl = ["text"].includes(ActiveToolbarId.ChildId);

  const ShowDrawControl = ["draw"].includes(ActiveToolbarId.ChildId);

  function calculateCropBounds(cropRect, image) {
    return {
      minLeft: image.left,
      minTop: image.top,
      maxLeft: image.left + image.getScaledWidth() - cropRect.getScaledWidth(),
      maxTop: image.top + image.getScaledHeight() - cropRect.getScaledHeight(),
    };
  }

  function applyCropBounds(cropRect, bounds) {
    cropRect.left = Math.min(
      Math.max(cropRect.left, bounds.minLeft),
      bounds.maxLeft
    );
    cropRect.top = Math.min(
      Math.max(cropRect.top, bounds.minTop),
      bounds.maxTop
    );
  }

  // Clean up event listeners when crop mode is exited
  const cleanupCropMode = () => {
    fabricRef.current.off("mouse:down");
    fabricRef.current.off("selection:created");
    setIsCropping(false);
  };

  const applyCrop = useCallback((cropRect, targetImage) => {
    const overlay = fabricRef.current
      .getObjects()
      .find((obj) => obj.id === "overlay-rect");
    if (overlay) {
      fabricRef.current.remove(overlay);
    }

    // Remove crop rectangle
    const cropRectangle = fabricRef.current
      .getObjects()
      .find((obj) => obj.id === "crop-rect");
    fabricRef.current.remove(cropRectangle);
    fabricRef.current.remove(cropRect);

    // Calculate new crop coordinates
    const scaledCropX = (cropRect.left - targetImage.left) / targetImage.scaleX;
    const scaledCropY = (cropRect.top - targetImage.top) / targetImage.scaleY;
    const scaledWidth = (cropRect.width * cropRect.scaleX) / targetImage.scaleX;
    const scaledHeight =
      (cropRect.height * cropRect.scaleY) / targetImage.scaleY;
    // Apply crop to image
    targetImage.set({
      cropX: scaledCropX,
      cropY: scaledCropY,
      width: scaledWidth,
      height: scaledHeight,
      top: targetImage.top + scaledCropY * targetImage.scaleY,
      left: targetImage.left + scaledCropX * targetImage.scaleX,
      selectable: false,
      hasControls: false,
      isProtected: true,
      cropped: true,
    });

    // Calculate new canvas dimensions based on the cropped image
    const newCanvasWidth = scaledWidth * targetImage.scaleX;
    const newCanvasHeight = scaledHeight * targetImage.scaleY;

    // Resize the canvas to match the cropped image dimensions
    fabricRef.current.setDimensions({
      width: newCanvasWidth,
      height: newCanvasHeight,
    });

    // Center the image on the resized canvas
    targetImage.center();

    fabricRef.current.renderAll();
    historyRef.current?.saveState();
    checkUndoRedoState();
  }, []);

  const disabledAllCanvasControl = () => {
    if (!fabricImage.current) {
      return;
    }

    fabricImage.current.setControlsVisibility({
      mt: false,
      mb: false,
      ml: false,
      mr: false,
      bl: false,
      br: false,
      tl: false,
      tr: false,
      mtr: false,
    });

    fabricImage.current.selectable = false;
    fabricImage.current.hasControls = false;
    fabricRef.current.selection = false;
    fabricRef.current?.discardActiveObject();

    Object.entries(renderFabricDefaultCorner).forEach(([key, render]) => {
      fabricImage.current.controls[key].render = render;
    });
  };

  const convertCanvasToBlob = useCallback(() => {
    return new Promise((resolve, reject) => {
      try {
        // Get data URL from canvas
        const dataUrl = fabricRef.current.toDataURL({
          multiplier: 1 / ImageScale,
          format: "png",
          quality: 1,
        });

        // Convert data URL to blob
        const binaryStr = atob(dataUrl.split(",")[1]);
        const length = binaryStr.length;
        const arr = new Uint8Array(length);

        for (let i = 0; i < length; i++) {
          arr[i] = binaryStr.charCodeAt(i);
        }

        const blob = new Blob([arr], { type: "image/png" });
        resolve(blob);
      } catch (error) {
        reject(error);
      }
    });
  }, [ImageScale]);

  const cropCurrentSelection = useCallback(() => {
    const existingCropRect = fabricRef.current
      .getObjects()
      .find((obj) => obj.id === "crop-rect");
    const existingOverlay = fabricRef.current
      .getObjects()
      .find((obj) => obj.id === "overlay-rect");

    if (existingCropRect) {
      // Manually trigger the deselected event to finalize the previous crop
      applyCrop(existingCropRect, fabricImage.current);
      if (existingOverlay) {
        fabricRef.current.remove(existingOverlay);
      }
      setIsCropping(false);
    }

    cleanupCropMode();
  }, [applyCrop]);

  const discardCurrentSelection = useCallback(() => {
    // Find and remove the crop rectangle and overlay
    const existingCropRect = fabricRef.current
      .getObjects()
      .find((obj) => obj.id === "crop-rect");
    const existingOverlay = fabricRef.current
      .getObjects()
      .find((obj) => obj.id === "overlay-rect");

    if (existingCropRect) {
      fabricRef.current.remove(existingCropRect);
      if (existingOverlay) {
        fabricRef.current.remove(existingOverlay);
      }

      // Reset the image to its original dimensions if it was temporarily expanded
      if (fabricImage.current._originalElement) {
        fabricImage.current.set({
          width: fabricImage.current._originalElement.naturalWidth,
          height: fabricImage.current._originalElement.naturalHeight,
        });
      }

      setIsCropping(false);
      fabricRef.current.renderAll();
    }
  }, []);

  const initCropMode = useCallback(
    (targetImage) => {
      if (IsCropping) {
        // Find existing crop rectangle and overlay
        cropCurrentSelection();
        return;
      }

      setIsCropping(true);

      // Create crop selection rectangle
      const cropRect = new fabric.Rect({
        id: "crop-rect",
        top: targetImage.top,
        left: targetImage.left,
        angle: targetImage.angle,
        width: targetImage.getScaledWidth(),
        height: targetImage.getScaledHeight(),
        stroke: "rgb(42, 67, 101)",
        strokeWidth: 2,
        strokeDashArray: [5, 5],
        fill: "rgba(255, 255, 255, 1)",
        globalCompositeOperation: "overlay",
        lockRotation: true,
        selectable: false,
      });

      // Create darkened overlay
      const overlay = new fabric.Rect({
        id: "overlay-rect",
        top: targetImage.top,
        left: targetImage.left,
        angle: targetImage.angle,
        width: targetImage.getScaledWidth(),
        height: targetImage.getScaledHeight(),
        selectable: false,
        selection: false,
        fill: "rgba(0, 0, 0, 0.5)",
        lockRotation: true,
      });

      // Store original crop values
      const originalCropX = targetImage.cropX;
      const originalCropY = targetImage.cropY;
      const originalWidth = targetImage.width;
      const originalHeight = targetImage.height;

      // Reset image to full size
      targetImage.set({
        cropX: null,
        cropY: null,
        left: targetImage.left - originalCropX * targetImage.scaleX,
        top: targetImage.top - originalCropY * targetImage.scaleY,
        width: targetImage._originalElement.naturalWidth,
        height: targetImage._originalElement.naturalHeight,
        dirty: false,
      });

      // Position crop rectangle
      cropRect.set({
        left: targetImage.left + originalCropX * targetImage.scaleX + 15,
        top: targetImage.top + originalCropY * targetImage.scaleY + 15,
        width: originalWidth * targetImage.scaleX - 30,
        height: originalHeight * targetImage.scaleY - 30,
        dirty: false,
        cornerColor: "#FB4764",
        borderColor: "transparent",
        cornerSize: 24,
        // cornerStyle: 'circle',
        transparentCorners: false,
      });

      cropRect.setControlsVisibility({
        mtr: false,
      });

      cropRect.controls.ml.render = renderCornerML;
      cropRect.controls.mr.render = renderCornerMR;
      cropRect.controls.mt.render = renderCornerMT;
      cropRect.controls.mb.render = renderCornerMB;
      cropRect.controls.tl.render = renderCornerTL;
      cropRect.controls.tr.render = renderCornerTR;
      cropRect.controls.bl.render = renderCornerBL;
      cropRect.controls.br.render = renderCornerBR;

      // Position overlay
      overlay.set({
        left: targetImage.left,
        top: targetImage.top,
        width: targetImage.width * targetImage.scaleX,
        height: targetImage.height * targetImage.scaleY,
        dirty: false,
      });

      // Store original scale
      cropRect.oldScaleX = cropRect.scaleX;
      cropRect.oldScaleY = cropRect.scaleY;

      // Add elements to canvas
      fabricRef.current.add(overlay);
      fabricRef.current.add(cropRect);
      fabricRef.current.discardActiveObject();
      fabricRef.current.setActiveObject(cropRect);
      fabricRef.current.renderAll();

      // Handle crop rectangle movement
      cropRect.on("moving", function () {
        const bounds = calculateCropBounds(cropRect, targetImage);
        applyCropBounds(cropRect, bounds);
      });

      fabricRef.current.on("mouse:down", function () {
        const cropRect = fabricRef.current
          .getObjects()
          .find((obj) => obj.id === "crop-rect");
        if (cropRect) {
          // If clicking empty space during crop mode, keep crop rectangle selected
          fabricRef.current.setActiveObject(cropRect);
          fabricRef.current.requestRenderAll();
        }
      });

      // Handle crop rectangle deselection
      cropRect.on("deselected", function (options) {
        if (!options.e && !IsCropping) {
          discardCurrentSelection();
        }
      });
    },
    [IsCropping, cropCurrentSelection, discardCurrentSelection]
  );

  const handleSelectChildAction = useCallback(
    (actionId, newSliderVal) => {
      if (!fabricRef.current) {
        return;
      }

      const objects = fabricRef.current.getObjects();

      if (actionId !== "crop") {
        disabledAllCanvasControl();
      }

      if (actionId !== "draw") {
        fabricRef.current.isDrawingMode = false;
        fabricRef.current.off("object:added");
      }

      if (actionId !== "eraser") {
        isEraserActive.current = false;
        fabricRef.current.defaultCursor = "auto";
        fabricRef.current.hoverCursor = fabricRef.current.defaultCursor;
      }

      switch (actionId) {
        // TRANSFORM CHILDREN
        case "crop": {
          initCropMode(fabricImage.current);
          break;
        }
        case "flipHorizontal": {
          objects.forEach((obj) => {
            obj.set("flipX", !obj.flipX);
          });
          historyRef.current?.saveState();
          checkUndoRedoState();
          break;
        }
        case "flipVertical": {
          objects.forEach((obj) => {
            obj.set("flipY", !obj.flipY);
          });
          historyRef.current?.saveState();
          checkUndoRedoState();
          break;
        }
        case "rotate": {
          objects.forEach((obj) => {
            obj.rotate((obj.angle || 0) + 90);
          });
          historyRef.current?.saveState();
          checkUndoRedoState();
          break;
        }

        // ENHANCE & FILTER CHILDREN
        case "brightness":
        case "contrast":
        case "saturation":
        case "temperature":
        case "grey":
        case "sepia":
        case "invert":
        case "blur": {
          const FiltersParams = newSliderVal || SliderVal[actionId] || 50;

          objects.forEach((obj) => {
            if (obj instanceof fabric.Image) {
              const filter = Filters[actionId](FiltersParams);
              obj.filters = [filter];
              obj.applyFilters();
            }
          });
          historyRef.current?.saveState();
          checkUndoRedoState();
          break;
        }

        // ANNOTATE CHILDREN
        case "draw": {
          fabricRef.current.isDrawingMode = true;
          fabricRef.current.freeDrawingBrush.width = DrawSize;
          fabricRef.current.freeDrawingBrush.color = DrawColor;

          fabricRef.current.on("object:added", function () {
            historyRef.current?.saveState();
            checkUndoRedoState();
          });
          break;
        }
        case "text": {
          const text = new fabric.IText("Click to edit", {
            left: 100,
            top: 100,
            fontFamily: "Inter",
            fontSize: TextSize,
            fill: TextColor,
            fontWeight: "normal",
            fontStyle: "normal",
          });
          fabricRef.current.add(text);
          fabricRef.current.setActiveObject(text);

          // Ensure only text objects are selectable
          fabricRef.current.getObjects().forEach((obj) => {
            if (obj instanceof fabric.IText) {
              obj.selectable = true;
              obj.hasControls = true;
            } else {
              obj.selectable = false;
              obj.hasControls = false;
            }
          });
          historyRef.current?.saveState();
          checkUndoRedoState();
          break;
        }
        case "eraser": {
          isEraserActive.current = true;
          fabricRef.current.defaultCursor =
            "url(/images/icons/cursor.png) 0 40, auto";
          fabricRef.current.hoverCursor = fabricRef.current.defaultCursor;
          historyRef.current?.saveState();
          checkUndoRedoState();
          break;
        }

        default: {
          break;
        }
      }

      fabricRef.current.renderAll();
    },
    [DrawColor, DrawSize, SliderVal, TextColor, TextSize, initCropMode]
  );

  const handleSelectToolbarId = useCallback(
    (SelectedToolbarId) => {
      // when selecting a children of a parent
      if (SelectedToolbarId.ChildId) {
        setActiveToolbarId(SelectedToolbarId);
        handleSelectChildAction(SelectedToolbarId.ChildId);
        return;
      }

      // when selecting active parent
      if (SelectedToolbarId.ParentId === ActiveToolbarId.ParentId) {
        setActiveToolbarId({
          ParentId: "",
          ChildId: "",
        });
        return;
      }

      // when selecting inactive parent
      setActiveToolbarId(SelectedToolbarId);
    },
    [ActiveToolbarId.ParentId, handleSelectChildAction]
  );

  const handleChangeSliderValue = useCallback(
    (newVal) => {
      setSliderVal((prev) => ({ ...prev, [ActiveToolbarId.ChildId]: newVal }));

      handleSelectChildAction(ActiveToolbarId.ChildId, newVal);
    },
    [ActiveToolbarId.ChildId, handleSelectChildAction]
  );

  const checkUndoRedoState = () => {
    const hasUndo = historyRef.current?.hasUndo();
    const hasRedo = historyRef.current?.hasRedo();
    setHasUndo(hasUndo);
    setHasRedo(hasRedo);
  };

  const handleUndoEditImage = useCallback(() => {
    historyRef.current.undo?.();
    checkUndoRedoState();
  }, []);

  const handleRedoEditImage = useCallback(() => {
    historyRef.current.redo?.();
    checkUndoRedoState();
  }, []);

  const init = useCallback(
    ({ forceInit = false }) => {
      if ((!fabricRef.current && canvasRef.current) || forceInit) {
        fabricRef.current = new fabric.Canvas(canvasRef.current);

        // Make wheel events passive
        fabricRef.current.upperCanvasEl.addEventListener(
          "wheel",
          function (e) {
            e.preventDefault();
          },
          { passive: true }
        );

        fabricRef.current.lowerCanvasEl.addEventListener(
          "wheel",
          function (e) {
            e.preventDefault();
          },
          { passive: true }
        );

        initCustomInvert();
        initCustomGrayscale();
        initCustomSepia();

        fabricRef.current.setDimensions({
          width: maxCanvasWidth,
          height: maxCanvasHeight,
        });
      }

      const image = new Image();

      image.onload = () => {
        if (fabricRef.current) {
          fabricRef.current.clear();
          const canvas = fabricRef.current;
          const scale = Math.min(
            maxCanvasWidth / image.width,
            maxCanvasHeight / image.height
          );

          const canvasWidth = image.width * scale;
          const canvasHeight = image.height * scale;

          setRenderedImageHeight(canvasHeight);
          fabricRef.current.setDimensions({
            width: canvasWidth,
            height: canvasHeight,
          });

          fabricImage.current = new fabric.Image(image, {
            cornerColor: "#FBCEB1",
            borderColor: "transparent",
            cornerSize: 24,
            cornerStyle: "circle",
            transparentCorners: false,
            selectable: false,
            isProtected: true,
          });
          fabricImage.current.scale(scale);
          canvas.add(fabricImage.current);
          historyRef.current = new UndoRedoHistory(canvas, fabricImage);
          setImageScale(scale);

          // initially disable all canvas control
          disabledAllCanvasControl();
        }
      };

      if (selectedProduct.imageUrl) {
        fetch(
          window.localStorage.getItem("BaseURL") + selectedProduct.imageUrl,
          {
            headers: {
              Authorization: "Bearer " + window.localStorage.getItem("Token"),
            },
          }
        )
          .then((res) => res.blob())
          .then((imgBlob) => {
            image.src = URL.createObjectURL(imgBlob);
          });
      }
    },
    [selectedProduct.imageUrl]
  );

  const handleRevert = useCallback(() => {
    fabricRef.current.dispose();
    setIsCropping(false);
    init({ forceInit: true });
    historyRef.current?.clear();
    setHasUndo(false);
    setHasRedo(false);
    setShowUnsavedChangesModal(false);
  }, [init]);

  const saveImage = useCallback(async () => {
    try {
      if (IsCropping) {
        cropCurrentSelection();
      }

      const imgBlob = await convertCanvasToBlob();

      const ModifyData = new FormData();
      ModifyData.append("productId", selectedProduct.id);
      ModifyData.append("imageEditedFile", imgBlob, "edited-image.png");

      const Req = {
        method: "POST",
        baseURL:
          window.localStorage.getItem("BaseURL") +
          "/services/projectmanagement/api/products/save-image",
        headers: {
          Authorization: "Bearer " + window.localStorage.getItem("Token"),
        },
        data: ModifyData,
      };

      const res = await axios(Req);

      // Revert data
      handleRevert();
      return res.data;
    } catch (_) {}
  }, [
    IsCropping,
    convertCanvasToBlob,
    cropCurrentSelection,
    handleRevert,
    selectedProduct.id,
  ]);

  const handleDiscardAndLeave = () => {
    handleRevert();
    setIsEditingImage(false);
  };

  const handleModalSave = () => {
    setShowUnsavedChangesModal(false);
    resetAITools();
    setGlobalLoading(true);
    saveImage()
      .then(() => {
        handleProductCreationSuccess({});
        setIsEditingImage(false);
      })
      .catch(console.error)
      .finally(() => setGlobalLoading(false));
  };

  // watch state change text selection
  useEffect(() => {
    if (fabricRef.current) {
      const activeObject = fabricRef.current.getActiveObject();
      if (activeObject && activeObject.type === "i-text") {
        activeObject.set("fontSize", TextSize);
        activeObject.set("fill", TextColor);
        fabricRef.current.requestRenderAll();
        historyRef.current?.saveState();
        checkUndoRedoState();
      }
    }
  }, [TextColor, TextSize]);

  // watch state change draw
  useEffect(() => {
    handleSelectChildAction("draw");
    // eslint-disable-next-line
  }, [DrawColor, DrawSize]);

  useEffect(() => {
    if (fabricRef.current) {
      fabricRef.current.on("mouse:down", function (options) {
        if (options.target && isEraserActive.current) {
          const clickedObject = options.target;

          if (!clickedObject.isProtected) {
            fabricRef.current.remove(clickedObject);
            historyRef.current?.saveState();
            checkUndoRedoState();
          }
        }
      });
    }
    // eslint-disable-next-line
  }, [isEraserActive.current]);

  useEffect(() => {
    init({ forceInit: false });
  }, [init]);

  useEffect(() => {
    return () => {
      if (IsCropping) {
        cleanupCropMode();
      }
    };
  }, [IsCropping]);

  useEffect(() => {
    if (selectedAITools && HasUndo) {
      setShowUnsavedChangesModal(true);
    } else if (selectedAITools) {
      setIsEditingImage(false);
    }
  }, [HasUndo, selectedAITools]);

  const contextValue = useMemo(
    () => ({
      isEditingImage,
      setIsEditingImage,
      canvasRef,
      ActiveToolbarId,
      RenderedImageHeight,
      SliderVal,
      TextSize,
      ShowSlider,
      ShowTextControl,
      setTextSize,
      handleSelectToolbarId,
      handleChangeSliderValue,
      TextColor,
      setTextColor,
      handleUndoEditImage,
      handleRedoEditImage,
      handleRevert,
      ShowDrawControl,
      DrawSize,
      setDrawSize,
      DrawColor,
      setDrawColor,
      saveImage,
      HasUndo,
      HasRedo,
      init,
      IsCropping,
      cropCurrentSelection,
      discardCurrentSelection,
      setShowUnsavedChangesModal,
    }),
    [
      isEditingImage,
      ActiveToolbarId,
      RenderedImageHeight,
      SliderVal,
      TextSize,
      ShowSlider,
      ShowTextControl,
      handleSelectToolbarId,
      handleChangeSliderValue,
      TextColor,
      handleUndoEditImage,
      handleRedoEditImage,
      handleRevert,
      ShowDrawControl,
      DrawSize,
      DrawColor,
      saveImage,
      HasUndo,
      HasRedo,
      init,
      IsCropping,
      cropCurrentSelection,
      discardCurrentSelection,
    ]
  );

  return (
    <ImageEditorContext.Provider value={contextValue}>
      {showUnsavedChangesModal && (
        <ImageEditorUnsavedChangesModal
          Save={handleModalSave}
          DisacardAndLeave={handleDiscardAndLeave}
          Close={() => {
            setSelectedAITools(null);
            setShowUnsavedChangesModal(false);
          }}
        />
      )}

      {children}
    </ImageEditorContext.Provider>
  );
};

export default ImageEditorContextProvider;
